'use client';

import {
  OFFER_TRANSACTION,
  OfferMultipleOptionsAddonProduct,
  OfferMultipleOptionsCoreProduct,
  OfferSingleOptionProduct
} from '@ag1/atlas';
import { fetcher } from '@portal/shared/utils/fetcher';
import { isPreviewRouteView } from '@portal/shared/utils/previewRouteHelpers';
import { PortalOffer } from '@portal/types/offer';
import { getOfferInvalidReason } from '@portal/shared/utils/offer/generic';
import { useRouter } from 'next/router';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { useLocale } from 'shared/hooks/useLocale';
import { getSanityLinkUrl } from 'shared/utils/getSanityLinkUrl';
import {
  CheckoutFlow,
  LinkOptions,
  LinkParam,
  PageContextProps,
  PartialPage,
  UnifiedButtonParam
} from './types';
import { isOfferlessCheckoutLink } from './utils';
import { FRICTIONLESS_PARAMS } from './constants';
import { useCheckoutHooks } from './hooks/checkout';

const { MultipleOptionsOffer } = OFFER_TRANSACTION;

export type PageContextType = ReturnType<typeof useCreatePageContext>;

// Default context values should allow `.stories.tsx` components to work
// properly.
const PageContext = createContext<PageContextType>({
  page: {} as PartialPage,
  getOfferCTALink: () => null,
  sectionsUsed: {
    offerAnchor: false,
    sanityOffer: false,
    builderOffer: false,
    navigation: false
  },
  offerCTALink: null,
  disablePrimaryCTAButtons: false,
  shouldDisableCTALink: (_link: LinkParam, _options: LinkOptions = {}) => false,
  shouldDisableUnifiedButton: (_button: UnifiedButtonParam) => false,
  maybeReplaceOfferlessCheckoutLink: (link: string | null | undefined) => link || undefined,
  offer: null,
  coreProducts: null,
  addonProducts: null,
  singleOptionProducts: null,
  baseOffer: null,
  baseOfferAddonProducts: null,
  offerInvalidReason: null,
  baseOfferCoreProducts: null,
  shopifyVariantsByStoreCode: null
});

export const usePageContext = (): PageContextType => {
  const context = useContext(PageContext);
  if (context === undefined) {
    throw new Error('usePageContext must be used within a PageProvider');
  }
  return context;
};

const useCreatePageContext = ({
  page,
  sectionsUsed,
  checkoutFlow,
  offerId,
  addonProducts: addonProductsProp,
  offer: offerProp,
  coreProducts: coreProductsProp,
  singleOptionProducts: singleOptionProductProps,
  baseOffer,
  baseOfferCoreProducts,
  baseOfferAddonProducts,
  shopifyVariantsByStoreCode,
  offerInvalidReason: offerInvalidReasonProp
}: PageContextProps) => {
  const router = useRouter();
  const locale = useLocale();
  const isPreviewRoute = isPreviewRouteView(router);

  const { parseBaBUrl, parseDirectCheckoutUrl } = useCheckoutHooks();

  const [offer, setOffer] = useState(offerProp);

  const [offerCTALink, setOfferCTALink] = useState<string | null | undefined>(null);

  const [isDirectCheckoutLinkSet, setIsDirectCheckoutLinkSet] = useState(false);
  const [directCheckoutLink, setDirectCheckoutLink] = useState<string | null | undefined>(null);

  const [coreProducts, setCoreProducts] = useState<
    OfferMultipleOptionsCoreProduct[] | null | undefined
  >(coreProductsProp);
  const [addonProducts, setAddonProducts] = useState<
    OfferMultipleOptionsAddonProduct[] | null | undefined
  >(addonProductsProp);
  const [singleOptionProducts, setSingleOptionProducts] = useState<
    OfferSingleOptionProduct[] | null | undefined
  >(singleOptionProductProps);

  const offerIdLoaded = useRef(offerProp?.id || null);

  const isMultipleOptionsOffer = useMemo(
    () => offer?.transaction_type === MultipleOptionsOffer,
    [offer]
  );

  const offerInvalidReason = useMemo(() => {
    if (offerInvalidReasonProp) {
      return offerInvalidReasonProp;
    }
    if (offer) {
      return getOfferInvalidReason(offer, locale);
    }
    return null;
  }, [locale, offer, offerInvalidReasonProp]);

  const disablePrimaryCTAButtons = useMemo(() => {
    return (
      page.frictionless === true &&
      FRICTIONLESS_PARAMS.length > 0 &&
      !FRICTIONLESS_PARAMS.every((param) => !!router.query[param]) &&
      !offerInvalidReason
    );
  }, [page.frictionless, router.query, offerInvalidReason]);

  const shouldDisableCTALink = useCallback(
    (link: LinkParam, { noEmpty = false }: LinkOptions = {}) => {
      if (!link) {
        return noEmpty && !disablePrimaryCTAButtons;
      }

      if (!disablePrimaryCTAButtons) {
        return false;
      }

      return (isMultipleOptionsOffer && isOfferlessCheckoutLink(link)) || link.endsWith('#offers');
    },
    [isMultipleOptionsOffer, disablePrimaryCTAButtons]
  );

  const shouldDisableUnifiedButton = useCallback(
    (button: UnifiedButtonParam) => {
      if (!button) {
        return false;
      }

      // eslint-disable-next-line default-case
      switch (button.switchButton) {
        case undefined:
        case 'goToOfferButton':
        case 'noButton':
        case 'scrollButton':
          return disablePrimaryCTAButtons;

        case 'link':
          return (
            shouldDisableCTALink(button.buttonLink || '') ||
            ('baseButtonLinkV2' in button &&
              shouldDisableCTALink(getSanityLinkUrl(button.baseButtonLinkV2?.btnLink, locale), {
                noEmpty: true
              }))
          );

        case 'openModalButton':
          if ('openModalSelect' in button) {
            switch (button.openModalSelect) {
              case 'ingredients':
                return false;

              default: {
                return false;
              }
            }
          } else {
            return false;
          }

        default:
          return false;
      }
    },
    [disablePrimaryCTAButtons, locale, shouldDisableCTALink]
  );

  const getOfferCTALink = useCallback(
    (options?: { fallbackNoOffer?: string; fallbackInvalidOffer?: string }) => {
      const usedOfferId = offerId ?? offer?.id;
      if (!usedOfferId) {
        return options?.fallbackNoOffer || null;
      }
      if (!isPreviewRoute && disablePrimaryCTAButtons) {
        return null;
      }
      if (
        offerInvalidReason !== null ||
        // a page should only be considered having an invalid offer if both
        // the offer section and the offer anchor are not rendered
        // if either is present, parse the URL as usual
        (!sectionsUsed?.builderOffer && !sectionsUsed?.offerAnchor) ||
        !offer
      ) {
        return options?.fallbackInvalidOffer || null;
      }

      // Default to 'bab' just in case a page was somehow saved without a checkout
      // flow.
      const safeCheckoutFlow = checkoutFlow ?? ({ type: 'bab' } satisfies CheckoutFlow);

      if (isMultipleOptionsOffer) {
        return parseBaBUrl(safeCheckoutFlow, usedOfferId);
      }

      return directCheckoutLink;
    },
    [
      isMultipleOptionsOffer,
      checkoutFlow,
      disablePrimaryCTAButtons,
      isPreviewRoute,
      offerId,
      offer,
      offerInvalidReason,
      sectionsUsed?.builderOffer,
      sectionsUsed?.offerAnchor,
      directCheckoutLink,
      parseBaBUrl
    ]
  );

  const maybeReplaceOfferlessCheckoutLink = useCallback(
    (link: string | null | undefined) => {
      if (page.use_pdp && page.pdp_url) {
        return page.pdp_url;
      }

      if (
        sectionsUsed?.builderOffer &&
        isMultipleOptionsOffer &&
        link &&
        isOfferlessCheckoutLink(link)
      ) {
        return getOfferCTALink({ fallbackInvalidOffer: link }) || undefined;
      }
      return link || undefined;
    },
    [getOfferCTALink, isMultipleOptionsOffer, sectionsUsed, page]
  );

  useEffect(() => {
    const updateOffer = async () => {
      let currentOffer = offerProp || offer;
      // Fetch offer only if it's not already loaded.
      if (offerId && offerIdLoaded.current !== offerId) {
        offerIdLoaded.current = offerId;
        const response = await fetcher.get<PortalOffer | undefined>(
          `/api/offer?id=${offerId}&locale=${locale}`,
          undefined,
          {
            omitCookies: true
          }
        );
        currentOffer = response.data;
      }

      if (!currentOffer) {
        setOffer(offerProp);
        setCoreProducts(coreProductsProp);
        setAddonProducts(addonProductsProp);
        return;
      }

      setOffer(currentOffer);
      setCoreProducts(
        // 'type' checks are necessary because of the way the data is fetched
        // between Atlas and Portal. Atlas should be passing data through the prop,
        // but Portal will fetch and we can use the 'type' directly.
        coreProductsProp ||
          (currentOffer.subscriptions || []).reduce(
            (acc, x) => ('type' in x && x.type ? [...acc, x.type] : acc),
            [] as OfferMultipleOptionsCoreProduct[]
          )
      );
      setAddonProducts(
        addonProductsProp ||
          (currentOffer.subscriptions || []).flatMap((i) =>
            i.addons
              .map((addon) =>
                'type' in addon && addon.type
                  ? (addon.type as OfferMultipleOptionsAddonProduct)
                  : null
              )
              .filter((x): x is OfferMultipleOptionsAddonProduct => x !== null)
          )
      );
      setSingleOptionProducts(singleOptionProductProps);
    };
    updateOffer();
  }, [
    addonProductsProp,
    locale,
    offer,
    offerId,
    offerProp,
    coreProductsProp,
    singleOptionProductProps
  ]);

  useEffect(() => {
    if (!isDirectCheckoutLinkSet && !isMultipleOptionsOffer) {
      setIsDirectCheckoutLinkSet(true);
      const fetchShopifyDirectCheckoutLink = async () => {
        if (
          !offer ||
          isMultipleOptionsOffer ||
          !singleOptionProducts ||
          !singleOptionProducts.length
        )
          return;
        const link = await parseDirectCheckoutUrl(
          offer,
          singleOptionProducts,
          shopifyVariantsByStoreCode
        );
        setDirectCheckoutLink(link);
      };
      fetchShopifyDirectCheckoutLink();
    }
  }, [
    offer,
    locale,
    isDirectCheckoutLinkSet,
    setIsDirectCheckoutLinkSet,
    isMultipleOptionsOffer,
    singleOptionProducts,
    setDirectCheckoutLink,
    parseDirectCheckoutUrl,
    shopifyVariantsByStoreCode
  ]);

  useEffect(() => {
    const link = getOfferCTALink({ fallbackInvalidOffer: '/offer/bab/1' });
    setOfferCTALink(link);
  }, [getOfferCTALink, setOfferCTALink]);

  return useMemo(
    () => ({
      page,
      getOfferCTALink,
      offerCTALink,
      sectionsUsed,
      disablePrimaryCTAButtons,
      shouldDisableUnifiedButton,
      shouldDisableCTALink,
      maybeReplaceOfferlessCheckoutLink,
      offer,
      coreProducts,
      addonProducts,
      singleOptionProducts,
      baseOffer,
      baseOfferAddonProducts,
      offerInvalidReason,
      baseOfferCoreProducts,
      shopifyVariantsByStoreCode
    }),
    [
      page,
      getOfferCTALink,
      offerCTALink,
      sectionsUsed,
      disablePrimaryCTAButtons,
      shouldDisableUnifiedButton,
      shouldDisableCTALink,
      maybeReplaceOfferlessCheckoutLink,
      offer,
      coreProducts,
      addonProducts,
      singleOptionProducts,
      baseOffer,
      baseOfferAddonProducts,
      offerInvalidReason,
      baseOfferCoreProducts,
      shopifyVariantsByStoreCode
    ]
  );
};

export const PageProvider: React.FC<PageContextProps & { children: React.ReactNode }> = ({
  children,
  ...restProps
}) => {
  const baseSectionsContext = useCreatePageContext(restProps);

  return <PageContext.Provider value={baseSectionsContext}>{children}</PageContext.Provider>;
};
